import type { GroupId, ViewModelAssetId, ViewModelId } from 'balus-common';
import { TRPCClientError } from '@trpc/client';
import { trpcClient } from '@framework/trpcClient';

export const MaxFileAssetSizeInMB = 10;
const MaxFileAssetSize = MaxFileAssetSizeInMB * 1_000_000;

type UploadedFileAsset = {
  assetId: ViewModelAssetId;
  filename: string;
  fileSize: number;
  contentType: string;
};

type FileAssetUploadResult =
  { ok: true; asset: UploadedFileAsset } | { ok: false; reason: 'overLimit' | 'forbidden' | 'failed' };

type FileAssetValidationError =
  { reason: 'emptyFile'; filename: string } | { reason: 'fileSizeLimit'; filename: string };

export class ViewModelFileAssetUploader {
  static validate(file: File): FileAssetValidationError | null {
    if (file.size === 0) return { reason: 'emptyFile', filename: file.name };
    if (file.size >= MaxFileAssetSize) return { reason: 'fileSizeLimit', filename: file.name };
    return null;
  }

  static async fitsCapacity(
    groupId: GroupId,
    viewModelId: ViewModelId,
    totalBytes: number
  ): Promise<{ ok: true; fits: boolean } | { ok: false; reason: 'forbidden' | 'signInRequired' | 'failed' }> {
    try {
      const { fits } = await trpcClient.viewModel.checkFileUploadCapacity.mutate({ groupId, viewModelId, totalBytes });
      return { ok: true, fits };
    } catch (error) {
      const code = this.errorCodeOf(error);
      if (code === 'FORBIDDEN') return { ok: false, reason: 'forbidden' };
      // サーバーは URL 共有の匿名ユーザーを通常ユーザーではないとして BAD_REQUEST で拒否する
      if (code === 'BAD_REQUEST') return { ok: false, reason: 'signInRequired' };
      return { ok: false, reason: 'failed' };
    }
  }

  static async upload(groupId: GroupId, viewModelId: ViewModelId, file: File): Promise<FileAssetUploadResult> {
    const formData = new FormData();
    formData.append('groupId', groupId);
    formData.append('viewModelId', viewModelId);
    formData.append('file', file);

    try {
      const asset = await trpcClient.viewModel.uploadFileAsset.mutate(formData);
      return { ok: true, asset };
    } catch (error) {
      const code = this.errorCodeOf(error);
      if (code === 'PAYLOAD_TOO_LARGE') return { ok: false, reason: 'overLimit' };
      if (code === 'FORBIDDEN') return { ok: false, reason: 'forbidden' };
      return { ok: false, reason: 'failed' };
    }
  }

  private static errorCodeOf(error: unknown): string | undefined {
    return error instanceof TRPCClientError ? error.shape?.data?.code : undefined;
  }
}
